import { ElementType, useMemo } from "react";
import { TreeViewBaseItem } from "@mui/x-tree-view";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import queryString from "query-string";
import { DatabaseIcon, TreeNodeIcon } from "~/assets/icons";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import SearchResponse, { TableEntity } from "~/types/interfaces/catalogResponse";

const { VITE_CATALOG_API } = import.meta.env;

type ItemProps = TreeViewBaseItem<{
  id: string;
  icon?: ElementType;
  label: string;
}>;

const buildTreeItems = (tables: TableEntity[]): ItemProps[] => {
  // Use a record to group nodes by database fully qualified name.
  const treeMap: Record<string, ItemProps> = {};

  tables.forEach((table) => {
    const databaseKey = table.database.fullyQualifiedName;

    // Create the database node if it doesn't exist.
    if (!treeMap[databaseKey]) {
      treeMap[databaseKey] = {
        id: table.database.fullyQualifiedName,
        icon: DatabaseIcon,
        label: table.database.name,
        children: [],
      };
    }
    const dbNode = treeMap[databaseKey];

    // Check if a schema node for this table's schema already exists.
    let schemaNode = dbNode.children?.find(
      (child) => child.id === table.databaseSchema.fullyQualifiedName
    );
    // If not, create a new schema node.
    if (!schemaNode) {
      schemaNode = {
        id: table.databaseSchema.fullyQualifiedName,
        icon: TreeNodeIcon,
        label: table.databaseSchema.name,
        children: [],
      };
      dbNode.children!.push(schemaNode);
    }

    // Create the table node using fullyQualifiedName as the id and name as the label.
    const tableNode: ItemProps = {
      id: table.fullyQualifiedName,
      label: table.name,
    };

    // Add the table node under the corresponding schema node.
    schemaNode.children!.push(tableNode);
  });

  // Return an array of database nodes, each containing their schema and table children.
  return Object.values(treeMap);
};

const useTablesHierarchy = (searchTerm: string) => {
  const debouncedSearch = useDebounce(searchTerm);
  const { data: fqns } = useDatasetTableFQNs();
  const qs = queryString.stringify({
    index: "catalog-api-table_search_index",
    q: debouncedSearch,
    size: 10000,
  });

  const resp = useFetcher<SearchResponse<TableEntity>>(
    fqns ? `${VITE_CATALOG_API}/search/query?${qs}` : null,
    {
      keepPreviousData: true,
    }
  );

  // Map and filter client side to avoid hitting limit on ES query url length
  const memoizedData = useMemo(() => {
    const tablesList = resp.data?.hits.hits
      .map(({ _source }) => _source)
      .filter(({ fullyQualifiedName }) => !fqns?.includes(fullyQualifiedName));

    if (tablesList) {
      return buildTreeItems(tablesList);
    }
  }, [resp.data]);

  return { ...resp, data: memoizedData };
};

export default useTablesHierarchy;
